import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError, api } from "@/lib/apiClient";
import {
  decodeEmployeeLoginParam,
  employeeAccountTypeLabel,
  employeeDisplayFromRecord,
  employeeLoginFromRecord,
  type HeroEmployeeRecord,
} from "@/lib/employees";
import { useCurrentUser } from "@/lib/auth";
import { useRealtimeSync } from "@/lib/useRealtime";
import {
  canViewEmployeeTimeAccount,
  userCanManageAbsenceBudgets,
  userCanManageOvertimeOpeningBalances,
  userCanManageZeiterfassungSettings,
} from "@/lib/zeiterfassungPermissions";
import { EmployeeAbsenceBudgetEditor } from "@/pages/admin/mitarbeiter/EmployeeAbsenceBudgetEditor";
import { EmployeeEntryDateEditor } from "@/pages/admin/mitarbeiter/EmployeeEntryDateEditor";
import { EmployeeOvertimeOpeningBalanceEditor } from "@/pages/admin/mitarbeiter/EmployeeOvertimeOpeningBalanceEditor";
import { EmployeeOvertimePayoutEditor } from "@/pages/admin/mitarbeiter/EmployeeOvertimePayoutEditor";
import { EmployeeWorkModelAssignmentsEditor } from "@/pages/admin/mitarbeiter/EmployeeWorkModelAssignmentsEditor";

function employeeFromLocationState(
  state: unknown,
  userLogin: string,
): HeroEmployeeRecord | null {
  if (!state || typeof state !== "object") return null;
  const employee = (state as { employee?: HeroEmployeeRecord }).employee;
  if (!employee || typeof employee !== "object") return null;
  const login = employeeLoginFromRecord(employee);
  return login === userLogin ? employee : null;
}

export function MitarbeiterDetailPage() {
  const { login: loginParam } = useParams<{ login: string }>();
  const location = useLocation();
  const userLogin = useMemo(
    () => (loginParam ? decodeEmployeeLoginParam(loginParam) : ""),
    [loginParam],
  );
  const { user } = useCurrentUser();
  const canManageBudgets = userCanManageAbsenceBudgets(user);
  const canManageOvertime =
    userCanManageOvertimeOpeningBalances(user) &&
    canViewEmployeeTimeAccount(user, userLogin);
  const canManageSettings = userCanManageZeiterfassungSettings(user);

  const stateEmployee = useMemo(
    () => (userLogin ? employeeFromLocationState(location.state, userLogin) : null),
    [location.state, userLogin],
  );

  const [employee, setEmployee] = useState<HeroEmployeeRecord | null>(stateEmployee);
  const [loading, setLoading] = useState(!stateEmployee);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async (options?: { silent?: boolean }) => {
    if (!userLogin) return;
    if (!options?.silent) setLoading(true);
    setError(null);
    try {
      const heroList = await api.get<HeroEmployeeRecord[]>("/hero/employees");
      const found =
        (Array.isArray(heroList) ? heroList : []).find(
          (emp) => employeeLoginFromRecord(emp) === userLogin,
        ) ?? null;
      if (!found) {
        setError("Mitarbeiter nicht gefunden.");
        setEmployee(null);
      } else {
        setEmployee(found);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Mitarbeiter konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, [userLogin]);

  useEffect(() => {
    if (stateEmployee) {
      setEmployee(stateEmployee);
      setLoading(false);
      setError(null);
      return;
    }
    void load();
  }, [load, stateEmployee]);

  // Nur die (schreibgeschützten) Stammdaten still nachladen – die Editor-Panels
  // unten haben eigene Formularzustände und werden bewusst nicht überschrieben.
  useRealtimeSync({
    domains: ["hero"],
    enabled: !!userLogin,
    onChange: () => void load({ silent: true }),
  });

  const display = employee ? employeeDisplayFromRecord(employee) : userLogin;

  if (!userLogin) {
    return (
      <AppShell title="Mitarbeiter" back={{ to: "/admin/mitarbeiter", label: "Mitarbeiterverwaltung" }}>
        <Alert variant="error" title="Ungültiger Link">
          Kein Mitarbeiter angegeben.
        </Alert>
      </AppShell>
    );
  }

  return (
    <AppShell
      title={display}
      subtitle={
        employee?.is_nachunternehmer
          ? `${employee?.email || userLogin} · Nachunternehmer`
          : employee?.email || userLogin
      }
      back={{ to: "/admin/mitarbeiter", label: "Mitarbeiterverwaltung" }}
      actions={
        <div className="flex items-center gap-2">
          {employee?.is_nachunternehmer ? (
            <span className="rounded-full bg-slate-500/15 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
              Nachunternehmer
            </span>
          ) : null}
          <Link
            to="/zeiterfassung"
            className="rounded-full border border-separator px-3 py-1.5 text-sm text-label-secondary transition hover:border-accent hover:text-accent"
          >
            Zeiterfassung
          </Link>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : error ? (
        <Alert variant="error" title="Fehler">
          {error}
        </Alert>
      ) : (
        <div className="space-y-4">
          <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-label-tertiary">
              Stammdaten
            </h2>
            <dl className="mt-3 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-label-tertiary">Login</dt>
                <dd className="font-medium text-label">{userLogin}</dd>
              </div>
              <div>
                <dt className="text-label-tertiary">E-Mail</dt>
                <dd className="font-medium text-label">{employee?.email || "—"}</dd>
              </div>
              <div>
                <dt className="text-label-tertiary">Konto-Typ</dt>
                <dd className="font-medium text-label">
                  {employeeAccountTypeLabel(employee?.account_type)}
                </dd>
              </div>
              <div>
                <dt className="text-label-tertiary">Hero User-ID</dt>
                <dd className="font-medium text-label">{employee?.user_id ?? "—"}</dd>
              </div>
            </dl>
          </div>

          {canManageSettings && (
            <>
              <EmployeeEntryDateEditor
                userLogin={userLogin}
                userDisplay={display}
                refreshKey={refreshKey}
                onChanged={() => setRefreshKey((k) => k + 1)}
              />
              <EmployeeWorkModelAssignmentsEditor
                userLogin={userLogin}
                userDisplay={display}
                refreshKey={refreshKey}
                onChanged={() => setRefreshKey((k) => k + 1)}
              />
            </>
          )}

          {canManageBudgets && (
            <EmployeeAbsenceBudgetEditor
              userLogin={userLogin}
              userDisplay={display}
              refreshKey={refreshKey}
              onChanged={() => setRefreshKey((k) => k + 1)}
            />
          )}

          {canManageOvertime && (
            <>
              <EmployeeOvertimeOpeningBalanceEditor
                userLogin={userLogin}
                userDisplay={display}
                refreshKey={refreshKey}
                onChanged={() => setRefreshKey((k) => k + 1)}
              />
              <EmployeeOvertimePayoutEditor
                userLogin={userLogin}
                userDisplay={display}
                refreshKey={refreshKey}
                onChanged={() => setRefreshKey((k) => k + 1)}
              />
            </>
          )}

          {!canManageBudgets && !canManageOvertime && !canManageSettings && (
            <Alert variant="info" title="Keine bearbeitbaren Bereiche">
              Für Ihre Rolle sind hier derzeit keine Einstellungen verfügbar.
            </Alert>
          )}
        </div>
      )}
    </AppShell>
  );
}
